import React from 'react';
import { Box, Typography } from '@heycater/design-system';
import { BoxProps, TypographyProps } from '@material-ui/core';
import Image, { StaticImageData } from 'next/image';
import { Trans, useTranslation } from 'react-i18next';
import styled, { useTheme } from 'styled-components';

import CheckIcon from '@icons/green-check.svg';
import boostYourCanteenImage from '@images/food-program-page/find-the-right-program/boost-your-canteen.png';
import dailyImage from '@images/food-program-page/find-the-right-program/daily.png';
import regularImage from '@images/food-program-page/find-the-right-program/regular.png';
import specialEventImage from '@images/food-program-page/find-the-right-program/special-events.png';
import { FoodProgramH2 } from 'static_pages/foodProgram/Utils';
import Container from 'system/components/Container';

const IDEAS = [
  {
    titleKey: 'findTheRightProgram.dailyCatering.title',
    benefitKeys: 'findTheRightProgram.dailyCatering.benefits',
    image: dailyImage,
  },
  {
    titleKey: 'findTheRightProgram.regularCatering.title',
    benefitKeys: 'findTheRightProgram.regularCatering.benefits',
    image: regularImage,
  },
  {
    titleKey: 'findTheRightProgram.specialCatering.title',
    benefitKeys: 'findTheRightProgram.specialCatering.benefits',
    image: specialEventImage,
  },
  {
    titleKey: 'findTheRightProgram.boostYourCanteen.title',
    benefitKeys: 'findTheRightProgram.boostYourCanteen.benefits',
    image: boostYourCanteenImage,
  },
];

const FindTheRightProgram = (props: BoxProps) => {
  const theme = useTheme();
  const { t } = useTranslation('food_program');

  return (
    <Box as="section" textAlign="center" {...props}>
      <Container size="sm">
        <FoodProgramH2 mb={6}>
          <Trans
            t={t}
            i18nKey="findTheRightProgram.title"
            components={[
              <Box key="1" as="span" color={theme.palette.secondary[400]} />,
            ]}
          />
        </FoodProgramH2>
      </Container>
      <Container size="md">
        <Ideas>
          {IDEAS.map(({ titleKey, benefitKeys, image }) => {
            const i18Benefits = t(benefitKeys, {
              returnObjects: true,
            }) as unknown;

            const benefits = Array.isArray(i18Benefits)
              ? (i18Benefits as string[])
              : [];

            return (
              <Idea key={titleKey}>
                <IdeaImage src={image} />

                <IdeaTitle>
                  <Trans
                    t={t}
                    i18nKey={titleKey}
                    components={{ '1': <br /> }}
                  />
                </IdeaTitle>

                <BenefitItems>
                  {benefits.map((benefit) => (
                    <Box key={benefit} alignSelf="flex-start" display="flex">
                      <CheckIcon />
                      <Typography fontSize={16} ml={1}>
                        {benefit}
                      </Typography>
                    </Box>
                  ))}
                </BenefitItems>
              </Idea>
            );
          })}
        </Ideas>
      </Container>
    </Box>
  );
};

const Ideas = styled.div`
  display: grid;
  gap: ${({ theme }) => theme.spacing(3)}px;

  align-items: start;
  justify-items: center;

  ${({ theme }) => theme.breakpoints.up('sm')} {
    grid-template-columns: 1fr 1fr;
  }

  ${({ theme }) => theme.breakpoints.up('md')} {
    grid-template-columns: 1fr 1fr 1fr 1fr;
    gap: ${({ theme }) => theme.spacing(4)}px;
  }

  ${({ theme }) => theme.breakpoints.up('lg')} {
    gap: ${({ theme }) => theme.spacing(9)}px;
  }
`;

const Idea = styled.div`
  flex-grow: 1;
  flex-shrink: 0;
  width: 200px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)}px;
`;

const IdeaImage = (props: { src: string | StaticImageData }) => (
  <Image src={props.src} width={160} height={158} objectFit="contain" alt="" />
);

const IdeaTitle = (props: TypographyProps) => (
  <Typography
    lineHeight={{ xs: 1.5, sm: 1.5 }}
    fontSize={20}
    component="h3"
    fontWeight={600}
    {...props}
  />
);

const BenefitItems = styled.div`
  display: flex;
  align-self: start;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(1)}px;
`;

export default FindTheRightProgram;
